import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Incident {
  id: string
  title: string
  severity: string
  status: string
  priority: string
  created_at: string
  resolved_at: string | null
  duration_mins: number | null
  commander: string
  affected_services: string[]
  impact: string
}

interface OnCallSchedule {
  id: string
  team: string
  current_primary: string
  current_secondary: string
  incidents_this_week: number
  avg_response_time_mins: number
}

interface IncidentStats {
  total_incidents: number
  active_incidents: number
  critical_active: number
  avg_resolution_time_mins: number
  mttr_mins: number
  total_postmortems: number
  pending_actions: number
}

const IncidentResponsePage = () => {
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [onCallSchedules, setOnCallSchedules] = useState<OnCallSchedule[]>([])
  const [stats, setStats] = useState<IncidentStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('active')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [incidentsRes, onCallRes, statsRes] = await Promise.all([
        fetch('/api/incidents/'),
        fetch('/api/incidents/on-call'),
        fetch('/api/incidents/statistics')
      ])

      setIncidents(await incidentsRes.json())
      setOnCallSchedules(await onCallRes.json())
      setStats(await statsRes.json())
    } catch (error) {
      console.error('Error fetching incident data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200'
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': return 'bg-green-100 text-green-800'
      case 'investigating': return 'bg-yellow-100 text-yellow-800'
      case 'identified': return 'bg-blue-100 text-blue-800'
      case 'monitoring': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const activeIncidents = incidents.filter(i => i.status !== 'resolved')
  const resolvedIncidents = incidents.filter(i => i.status === 'resolved')

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Incident Response</h1>
          <p className="text-gray-600">Manage incidents, postmortems, and on-call schedules</p>
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
              <p className="text-sm text-gray-500">Active Incidents</p>
              <p className="text-3xl font-bold">{stats.active_incidents}</p>
              <p className="text-xs text-red-600">{stats.critical_active} critical</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
              <p className="text-sm text-gray-500">MTTR</p>
              <p className="text-3xl font-bold">{stats.mttr_mins}</p>
              <p className="text-xs text-gray-500">minutes avg</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
              <p className="text-sm text-gray-500">Total Incidents</p>
              <p className="text-3xl font-bold">{stats.total_incidents}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500">
              <p className="text-sm text-gray-500">Pending Actions</p>
              <p className="text-3xl font-bold">{stats.pending_actions}</p>
              <p className="text-xs text-gray-500">from postmortems</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                {['active', 'resolved'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-6 text-sm font-medium capitalize ${
                      activeTab === tab
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab} ({tab === 'active' ? activeIncidents.length : resolvedIncidents.length})
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
              {(activeTab === 'active' ? activeIncidents : resolvedIncidents).map((incident) => (
                <div key={incident.id} className={`border rounded-lg p-4 ${getSeverityColor(incident.severity)}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium">{incident.title}</h3>
                      <p className="text-sm opacity-75">Commander: {incident.commander}</p>
                    </div>
                    <div className="flex gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(incident.status)}`}>
                        {incident.status}
                      </span>
                      <span className="px-2 py-1 rounded text-xs bg-gray-800 text-white">
                        {incident.priority}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 flex gap-4 text-xs">
                    <span>Impact: {incident.impact}</span>
                    <span>Services: {incident.affected_services.join(', ')}</span>
                    {incident.duration_mins && <span>Duration: {incident.duration_mins} mins</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold">On-Call Schedule</h2>
            </div>
            <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
              {onCallSchedules.map((schedule) => (
                <div key={schedule.id} className="border rounded-lg p-3">
                  <h3 className="font-medium text-sm">{schedule.team}</h3>
                  <div className="mt-2 text-xs text-gray-600 space-y-1">
                    <p>Primary: {schedule.current_primary}</p>
                    <p>Secondary: {schedule.current_secondary}</p>
                    <div className="flex justify-between mt-2 pt-2 border-t">
                      <span>{schedule.incidents_this_week} incidents</span>
                      <span>{schedule.avg_response_time_mins} min avg response</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default IncidentResponsePage
